import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { adminFetchEmailLog } from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";
import { EMAIL_STATUS_LABELS, OriginBadges, emailKindLabel, fmtDateTime } from "./email-log-list";

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";

// Preview van een verstuurde mail. De gerenderde HTML gaat in een iframe met
// `srcdoc` en een leeg `sandbox`, zodat niets uit de mail scripts kan draaien
// of bij onze sessie kan. Bij `origin: live` of `resend_import` is de mail
// precies wat Resend heeft verstuurd; bij `reconstructed` is hij achteraf
// nagebouwd en staat er een waarschuwing boven.
const EmailPreviewModal = ({ isOpen, emailLogId, onClose }) => {
  const [log, setLog] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen || !emailLogId) {
      setLog(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    adminFetchEmailLog(emailLogId)
      .then((res) => {
        if (!cancelled) setLog(res?.data ?? res);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error ?? "Mail laden mislukt.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, emailLogId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const status = EMAIL_STATUS_LABELS[log?.status] ?? null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Voorbeeld van verstuurde mail"
        className="my-6 flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200"
        style={{ maxHeight: "calc(100vh - 3rem)" }}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-900">
              {log?.subject ?? "Mail"}
            </h3>
            {log ? (
              <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-gray-600">
                <dt className="text-gray-400">Aan</dt>
                <dd className="truncate">
                  {log.recipientName && log.audience !== "internal"
                    ? `${log.recipientName} <${log.to}>`
                    : log.to}
                </dd>
                {log.audience === "internal" && log.recipientName ? (
                  <>
                    <dt className="text-gray-400">Betreft</dt>
                    <dd className="truncate">{log.recipientName}</dd>
                  </>
                ) : null}
                <dt className="text-gray-400">Van</dt>
                <dd className="truncate">{log.from}</dd>
                <dt className="text-gray-400">Verstuurd</dt>
                <dd>
                  {fmtDateTime(log.sentAt ?? log.createdAt)}
                  {" · "}
                  {emailKindLabel(log.kind)}
                  {log.actor?.naam ? ` · door ${log.actor.naam}` : ""}
                  {log.source === "cron" ? " · automatisch" : ""}
                </dd>
                {log.attachments?.length ? (
                  <>
                    <dt className="text-gray-400">Bijlagen</dt>
                    <dd>
                      {log.attachments.map((a) => a.filename).join(", ")}
                      <span className="text-gray-400"> (niet opgeslagen, alleen de naam)</span>
                    </dd>
                  </>
                ) : null}
                {log.status === "failed" ? (
                  <>
                    <dt className="text-rose-500">Fout</dt>
                    <dd className="text-rose-700">{log.error ?? "Onbekende fout"}</dd>
                  </>
                ) : null}
              </dl>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {status ? (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}
              >
                {status.label}
              </span>
            ) : null}
            {log ? <OriginBadges row={log} /> : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900"
              aria-label="Sluiten"
            >
              ✕
            </button>
          </div>
        </div>

        {log?.origin === "reconstructed" ? (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-2.5 text-xs text-amber-900">
            <strong>Nagebouwd, niet de originele mail.</strong> Deze regel is op{" "}
            {fmtDate(log.reconstructedWith?.renderedAt)} gereconstrueerd uit onze eigen
            gegevens
            {log.reconstructedWith?.templateDate
              ? ` met de mailtemplate van ${fmtDate(log.reconstructedWith.templateDate)}`
              : ""}
            {" "}en de dossiergegevens van dat moment. De mail die de ontvanger
            destijds kreeg kan hiervan afwijken.
          </div>
        ) : null}

        <div className="min-h-[300px] flex-1 overflow-hidden bg-gray-100">
          {loading ? (
            <div className="p-10 text-center text-sm text-gray-500">Laden…</div>
          ) : error ? (
            <div className="p-10 text-center text-sm text-rose-700">{error}</div>
          ) : log && log.htmlStored === false && log.textPreview ? (
            <div className="mx-auto max-w-2xl p-8 text-sm text-gray-700">
              <p className="mb-3 text-xs text-gray-500">
                Van deze mail is alleen het begin van de tekst bewaard (de eerste
                200 tekens uit het activiteitenlogboek), niet de volledige inhoud.
              </p>
              <blockquote className="rounded-lg border border-gray-200 bg-white p-4 italic">
                {log.textPreview}
              </blockquote>
            </div>
          ) : log && log.htmlStored === false ? (
            <div className="p-10 text-center text-sm text-gray-500">
              {log.origin === "reconstructed"
                ? "Van deze mail is alleen vastgelegd dat hij verstuurd is; de inhoud is niet bewaard."
                : "De inhoud van deze mail is bewust niet opgeslagen: hij bevat een persoonlijke link (wachtwoord-reset of profiel afronden)."}
            </div>
          ) : log?.html ? (
            <iframe
              title="Mail-voorbeeld"
              srcDoc={log.html}
              sandbox=""
              className="h-[70vh] w-full border-0 bg-white"
            />
          ) : (
            <div className="p-10 text-center text-sm text-gray-500">
              Geen inhoud opgeslagen voor deze mail.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EmailPreviewModal;
